import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { createProjectRequestSchema, type Project, type TipoObra } from '@builder/shared';
import { api, ApiError } from '@/api/client';
import { AppShell } from '@/components/layout/AppShell';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { EmptyStateIllustration } from '@/components/ui/EmptyStateIllustration';
import { PageSkeleton } from '@/components/ui/PageSkeleton';
import { PageErrorState } from '@/components/ui/PageErrorState';
import { TimedOutState } from '@/components/ui/TimedOutState';
import { useTimedQuery } from '@/hooks/useTimedQuery';
import { Input, Textarea } from '@/components/ui/Input';
import { AddressAutocomplete, type SelectedPlace } from '@/components/maps/AddressAutocomplete';
import { ProjectCard } from '@/components/ProjectCard';
import { TipoObraField } from '@/components/projects/TipoObraField';
import { useOnboardingStep } from '@/onboarding/useOnboardingStep';
import { ProjectsOverview, sortProjectsForList } from '@/components/ProjectsOverview';
import { useAuth } from '@/store/auth.store';
import { useT } from '@/hooks/useT';
import { cn } from '@/lib/cn';
import { NextActionCard } from '@/components/ui/NextActionCard';
import { useNextAction } from '@/hooks/useNextAction';
import { InlineSearch } from '@/components/ui/InlineSearch';
import { PageHeader } from '@/components/layout/PageHeader';
// Wave help-tour-mvp · 2026-05-20 — auto-trigger tour after first project
// is created (so PM new arrives, creates, and immediately sees the tour
// instead of being dropped into an empty project shell).
import { openTour } from '@/components/onboarding/useTourTrigger';
import { isTourCompleted } from '@/components/onboarding/Tour';

type StatusFilter = 'all' | 'active' | 'on_hold' | 'completed';

export default function ProjectsPage() {
  const { t, locale } = useT();
  const isEn = locale === 'en';
  const user = useAuth((s) => s.user);
  // PM-FRICTION F8 · 2026-05-20 — navegação React Router em vez de
  // `window.location.href` no NextActionCard. Antes, clicar na CTA
  // primária do "o que faço agora" disparava full-page reload — o que
  // o PM ansioso lê como "o app travou" no momento mais crítico.
  const topNavigate = useNavigate();
  const canCreate = user && (user.role === 'admin' || user.role === 'project_manager');

  const { data, isLoading, isError, refetch, timedOut } = useTimedQuery(useQuery({
    queryKey: ['projects'],
    queryFn: () => api.listProjects(),
    staleTime: 15_000,
  }));

  const { action: nextAction, isLoading: nextActionLoading } = useNextAction('projects');

  // W124.1 · 2026-05-26 — BUG-3 fix. CreateProjectSheet legado deprecated.
  // CTAs "Nova obra"/"Cadastre sua primeira obra" agora navegam pro wizard
  // novo em /projects/novo. O sheet legado ainda existe abaixo (rollback
  // safety) mas nenhum CTA o aciona. `creating` mantido pra não quebrar
  // testes legados; nunca é setado true daqui pra frente.
  const [creating, setCreating] = useState(false);
  const goToCreate = () => topNavigate('/projects/novo');
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');

  // Wave WelcomeFlow · 2026-05-20 — first-time PM redirect.
  // Quando o usuário nunca passou pela welcome (welcomedAt = null) E a
  // lista de projetos vem vazia, redireciona pra /welcome. Só dispara
  // depois do load (não chama Navigate durante isLoading pra evitar
  // flash de redirect indevido enquanto a query está em flight).
  // Early return abaixo de TODOS os hooks pra honrar Rules of Hooks.
  const shouldRedirectToWelcome =
    !isLoading && !isError && data && data.length === 0 && user && !user.welcomedAt;

  const filtered = useMemo(() => {
    if (!data) return [];
    const sorted = sortProjectsForList(data);
    let result = sorted;
    if (filter !== 'all') {
      if (filter === 'active') {
        result = result.filter((p) => p.status === 'in_progress' || p.status === 'planning');
      } else {
        result = result.filter((p) => p.status === filter);
      }
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          (p.address ?? '').toLowerCase().includes(q) ||
          (p.clientName ?? '').toLowerCase().includes(q),
      );
    }
    return result;
  }, [data, filter, search]);

  if (shouldRedirectToWelcome) {
    return <Navigate to="/welcome" replace />;
  }

  return (
    <AppShell>
      <PageHeader
        title={t.projects.title}
        subtitle={isEn ? 'Manage your active projects' : 'Gerencie suas obras em andamento'}
        primaryAction={
          canCreate
            ? { label: t.projects.newProject, onClick: goToCreate }
            : undefined
        }
        className="mb-5"
        data-testid="projects-page-header"
      />

      {/* DP1 — NextActionCard: answers "o que faço agora?" */}
      {nextActionLoading ? (
        <NextActionCard
          skeleton
          title=""
          primaryAction={{ label: '', onClick: () => {} }}
        />
      ) : nextAction ? (
        <NextActionCard
          title={nextAction.title}
          impact={nextAction.impact}
          severity={nextAction.severity}
          primaryAction={{
            label: nextAction.primaryAction.label,
            onClick: () => {
              if (nextAction.primaryAction.href) {
                topNavigate(nextAction.primaryAction.href);
              }
            },
          }}
          secondaryActions={nextAction.secondaryActions?.map((a) => ({
            label: a.label,
            onClick: () => {
              if (a.href) topNavigate(a.href);
            },
          }))}
        />
      ) : null}

      {timedOut ? (
        <TimedOutState onRetry={refetch} />
      ) : isLoading ? (
        <PageSkeleton />
      ) : isError ? (
        <PageErrorState
          onRetry={() => refetch()}
          iconVariant="error"
        />
      ) : !data || data.length === 0 ? (
        <EmptyState
          illustrationKey="projects"
          title={t.emptyState.projects.title}
          description={t.emptyState.projects.description}
          primaryAction={
            canCreate
              ? { label: t.emptyState.projects.primaryAction, onClick: goToCreate }
              : undefined
          }
          feedsInto={t.emptyState.projects.feedsInto}
        />
      ) : (
        <>
          <ProjectsOverview projects={data} className="mb-4" />

          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
            <FilterTabs value={filter} onChange={setFilter} projects={data} />
            <div className="sm:ml-auto sm:w-56">
              <InlineSearch
                value={search}
                onChange={setSearch}
                placeholder={isEn ? 'Search projects…' : 'Buscar obras…'}
                total={data.length}
                filtered={filtered.length}
              />
            </div>
          </div>

          {filtered.length === 0 ? (
            <EmptyState title={t.common.notFound} description={t.projects.emptyHint} />
          ) : (
            <ProjectsGrid items={filtered} />
          )}
        </>
      )}

      {user?.role === 'client' && (
        <Card className="mt-6">
          <CardBody>
            <p className="text-sm text-ink-muted">
              <Link
                to="/owner"
                className="font-medium text-ink underline-offset-4 hover:underline"
              >
                {t.owner.portalTitle}
              </Link>
            </p>
          </CardBody>
        </Card>
      )}

      {creating && <CreateProjectSheet onClose={() => setCreating(false)} />}
    </AppShell>
  );
}

// --- filter tabs -----------------------------------------------------------

function FilterTabs({
  value,
  onChange,
  projects,
}: {
  value: StatusFilter;
  onChange: (v: StatusFilter) => void;
  projects: Project[];
}) {
  const { t } = useT();
  const counts = useMemo(() => {
    const c = { all: projects.length, active: 0, on_hold: 0, completed: 0 } as Record<
      StatusFilter,
      number
    >;
    for (const p of projects) {
      if (p.status === 'in_progress' || p.status === 'planning') c.active += 1;
      if (p.status === 'on_hold') c.on_hold += 1;
      if (p.status === 'completed') c.completed += 1;
    }
    return c;
  }, [projects]);

  const tabs: Array<{ key: StatusFilter; label: string }> = [
    { key: 'all', label: t.projects.filterAll },
    { key: 'active', label: t.projects.filterActive },
    { key: 'on_hold', label: t.projects.filterOnHold },
    { key: 'completed', label: t.projects.filterCompleted },
  ];

  return (
    <div
      role="tablist"
      className="mb-4 flex gap-1 overflow-x-auto rounded-xl border border-line bg-surface-3 p-1"
    >
      {tabs.map((tab) => {
        const active = tab.key === value;
        return (
          <button
            type="button"
            key={tab.key}
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.key)}
            className={cn(
              'pressable inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium transition-colors duration-160 ease-out-soft',
              active
                ? 'bg-surface text-ink shadow-xs dark:shadow-xs-dark'
                : 'text-ink-muted hover:text-ink',
            )}
          >
            <span>{tab.label}</span>
            <span
              className={cn(
                'inline-flex min-w-[1.25rem] justify-center rounded-full px-1.5 py-px text-[11px] tabular-nums',
                active ? 'bg-surface-3 text-ink' : 'bg-surface/60 text-ink-subtle',
              )}
            >
              {counts[tab.key]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// --- create sheet ----------------------------------------------------------

// W124.1 · 2026-05-26 — DEPRECATED: usar WizardObraPage em /projects/novo.
// Mantido como fallback (rollback safety). Nenhum CTA referencia mais este
// componente; ele só renderiza se `creating` for true, o que não acontece
// mais via UI normal. Remover na W125+ depois de N dias em prod sem regressões.
/** @deprecated use WizardObraPage em /projects/novo */
function CreateProjectSheet({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [description, setDescription] = useState('');
  // Wave 14 (Onda C) · B10 end-to-end. Tipo de obra opcional na criação.
  // Null = não informado; o wizard mostra picker em modo lista única.
  const [tipoObra, setTipoObra] = useState<TipoObra | null>(null);
  // Wave geo · 2026-05-21 — quando o PM seleciona um lugar do autocomplete
  // armazenamos lat/lng/placeId pra enviar junto no POST e pular a 2ª chamada
  // de geocoding no backend. Resetado quando o PM edita o endereço manualmente.
  const [picked, setPicked] = useState<SelectedPlace | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // ESC closes the sheet. Keeps keyboard users first-class.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const mutation = useMutation({
    mutationFn: (vars: Parameters<typeof api.createProject>[0]) => api.createProject(vars),
    onSuccess: (project) => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      onClose();
      // W1F · 2026-05-12 · pós-criação manda pra Visão Geral em vez
      // de orçamento. OnboardingGuide mostra 3 caminhos (Trena, Docs,
      // Orçamento manual) · Trena é o destaque · usuário escolhe.
      // Mantém a query string `?welcome=1` pra qualquer outra tela que
      // queira aproveitar o sinal de "novo projeto".
      if (project?.id) {
        navigate(`/projects/${project.id}?welcome=1`);
        // Wave help-tour-mvp · 2026-05-20 — auto-trigger tour APENAS na
        // primeira criação (não rodou ainda). Delay curto pra navegação
        // assentar antes do overlay subir.
        if (!isTourCompleted()) {
          window.setTimeout(() => openTour(), 600);
        }
      }
    },
    // PM-FRICTION-AUDIT 2026-05-20 · F1 fix. Antes esse onError disparava
    // window.alert(...) — primeiro popup OS-native que o PM novo via no
    // app, na ação mais crítica do cadastro. Removido: o `mutation.error`
    // já renderiza inline mais abaixo (banner vermelho dentro do sheet)
    // via {mutation.error && <div ...>...}. Sem dialog nativo no fluxo.
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const parsed = createProjectRequestSchema.safeParse({
      name,
      address,
      clientName,
      clientEmail: clientEmail || undefined,
      description: description || undefined,
      // Wave 14 (Onda C) · B10 end-to-end. Só envia tipoObra quando o PM
      // realmente escolheu — null aqui = "não informado" e o backend
      // persiste NULL na coluna.
      tipoObra: tipoObra ?? undefined,
      // Wave geo · 2026-05-21 — quando o PM clicou numa sugestão do
      // autocomplete, mandamos lat/lng/placeId no payload pra o backend
      // pular o geocoding. Se foi digitado livre (picked=null OU address
      // ≠ picked.address depois de editar), backend geocoda no servidor.
      lat: picked && picked.address === address ? picked.lat : undefined,
      lng: picked && picked.address === address ? picked.lng : undefined,
      placeId: picked && picked.address === address ? picked.placeId : undefined,
    });
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      setErrors({
        name: flat.name?.[0] ?? '',
        address: flat.address?.[0] ?? '',
        clientName: flat.clientName?.[0] ?? '',
        clientEmail: flat.clientEmail?.[0] ?? '',
        tipoObra: flat.tipoObra?.[0] ?? '',
      });
      return;
    }
    setErrors({});
    mutation.mutate(parsed.data);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 backdrop-blur-sm animate-fade-in-up sm:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={cn(
          'w-full border border-line bg-surface-2 shadow-pop dark:shadow-pop-dark',
          'rounded-t-2xl animate-scale-in sm:max-w-md sm:rounded-2xl',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-line px-5 py-3.5">
          <h2 className="text-lg font-semibold tracking-tight text-ink">{t.projects.newProject}</h2>
          <button
            type="button"
            onClick={onClose}
            className="pressable text-sm text-ink-muted hover:text-ink"
            aria-label={t.common.close}
          >
            {t.common.close}
          </button>
        </div>
        <form onSubmit={onSubmit} className="space-y-4 p-5">
          <Input
            label={t.projects.title}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            error={errors.name}
            autoFocus
            required
          />
          {/* Wave geo · 2026-05-21 — substitui o <Input> simples por
              autocomplete Google Places. Lazy-load do SDK + fallback pra
              input livre se o script não carregar (degradação graciosa).
              Quando o PM seleciona uma sugestão, capturamos lat/lng/placeId
              pra evitar a 2ª chamada geocoding no backend. */}
          <AddressAutocomplete
            label={t.projects.address}
            value={address}
            onChange={(v) => {
              setAddress(v);
              // PM editou após selecionar: invalida o pick (a lat/lng não
              // bate mais com o texto).
              if (picked && picked.address !== v) setPicked(null);
            }}
            onPlaceSelected={(p) => {
              setAddress(p.address);
              setPicked(p);
            }}
            error={errors.address}
            hint={picked && picked.address === address ? t.maps.geoCaptured : undefined}
            required
            data-testid="create-project-address"
          />
          <Input
            label={t.projects.clientName}
            value={clientName}
            onChange={(e) => setClientName(e.currentTarget.value)}
            error={errors.clientName}
            required
          />
          <Input
            label={t.projects.clientEmail}
            type="email"
            value={clientEmail}
            onChange={(e) => setClientEmail(e.currentTarget.value)}
            error={errors.clientEmail}
          />
          <Textarea
            label={t.projects.notes}
            value={description}
            onChange={(e) => setDescription(e.currentTarget.value)}
          />
          {/* Wave 14 (Onda C) · B10 end-to-end. Tipo de obra opcional —
              destrava recomendação de templates no First Run Wizard do
              Trena. PM novo pode pular sem fricção. */}
          <TipoObraField
            value={tipoObra}
            onChange={setTipoObra}
            error={errors.tipoObra || undefined}
            disabled={mutation.isPending}
            testId="create-project-tipo-obra"
          />
          {mutation.error && (
            <div className="animate-fade-in-up rounded-lg border border-err/30 bg-err/10 px-3 py-2 text-sm text-err">
              {mutation.error instanceof ApiError
                ? mutation.error.message
                : t.common.somethingWentWrong}
            </div>
          )}
          {/* Wave 33 — sticky action bar: safe-area aware on iOS PWA install. */}
          <div className="sticky -bottom-5 -mx-5 flex gap-2 border-t border-line bg-surface-2/95 px-5 py-3 backdrop-blur [padding-bottom:max(0.75rem,env(safe-area-inset-bottom))]">
            <Button type="button" variant="secondary" onClick={onClose} fullWidth>
              {t.common.cancel}
            </Button>
            <Button type="submit" loading={mutation.isPending} fullWidth>
              {t.projects.createProject}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Onboarding · register the projects list container and the FIRST card
// as tour targets so the financeiro/PM beacons land on the right thing.
//
// Wave 283 — the per-card delete affordance (`#262` overlay) was
// removed. The grid now only does navigation; deleting an obra is a
// project-detail-footer action (Visão Geral → Zona de perigo).
function ProjectsGrid({
  items,
}: {
  items: Array<{ id: string; name: string; address?: string | null }> & unknown;
}) {
  const { beaconRef: listRef } = useOnboardingStep('projects-list');
  const { beaconRef: cardRef } = useOnboardingStep('project-card');
  // The hooks return generic `HTMLElement` callback refs; div is fine.
  return (
    <div ref={listRef as unknown as React.Ref<HTMLDivElement>} data-tour="projects-list" className="grid gap-3 sm:grid-cols-2">
      {/* Wave 283 — the per-card trash icon was removed from this list.
          Six tiny floating buttons across the grid created click-hazard
          ("clipo") for users who actually wanted to OPEN a project.
          The danger-zone DeleteProjectButton now lives in the project
          detail footer (VisaoGeralPage), GitHub-style: you must navigate
          INTO the obra to be allowed to delete it. */}
      {(items as Array<{ id: string; name: string }>).map((p, i) => (
        <div
          key={p.id}
          ref={i === 0 ? (cardRef as unknown as React.Ref<HTMLDivElement>) : undefined}
          data-tour={i === 0 ? 'project-card' : undefined}
          className="animate-fade-in-up"
          style={{ animationDelay: `${Math.min(i, 6) * 40}ms` }}
        >
          <ProjectCard project={p as never} />
        </div>
      ))}
    </div>
  );
}
